/* eslint-disable lit/no-invalid-html, lit/binding-positions */
import { FormControlMixin } from '@open-wc/form-control';
import { nothing, PropertyValueMap, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { html, literal } from 'lit/static-html.js';
import {
  BaseElementWithGlobalStyles,
  customElement,
  FormControl,
  FormControlController,
  NonCheckableFormControl,
  SlotObserver,
  validValidityState,
} from '../../base-element';
import { Translate } from '../../base-element/mixins/translation-mixin';
import buttonStyles from './button.scss?inline';

// demonstrating how global var definitions work
import buttonVars1 from './button.vars1.scss?inline';
import buttonVars2 from './button.vars2.scss?inline';

export const ButtonVariants = {
  primary: 'primary',
  secondary: 'secondary',
  'secondary-hv': 'secondary-hv',
  ghost: 'ghost',
  warning: 'warning',
  danger: 'danger',
  'validation-destructive': 'validation-destructive',
  'validation-warning': 'validation-warning',
  'validation-success': 'validation-success',
  'validation-neutral': 'validation-neutral',
} as const;

export type ButtonVariant = keyof typeof ButtonVariants;

/**
 * @element ps-button
 *
 * @cssprop --ps-button--icon-size - adjusts the size of icons in the `prefix` and `suffix` slots
 * @cssprop --ps-button--icon-spacing - adjusts the space between slotted icons and button text
 */
/**
 * A boolean that is allowed to be ABSENT.
 *
 * Lit's default boolean converter cannot express three states: `toAttribute`
 * is `value ? '' : null`, so `false` and `undefined` both remove the attribute,
 * and `fromAttribute` is `value !== null`, so `expanded="false"` in plain HTML
 * reads back as `true`. For `aria-expanded` / `aria-pressed` that erases the
 * distinction the property exists for — "collapsed" is a state that must be
 * announced, "controls nothing" must render no attribute at all. Consumers
 * going through `@lit/react` set properties and never saw it; consumers writing
 * markup did.
 */
const triStateBoolean = {
  toAttribute: (value: boolean | undefined) =>
    value === undefined ? null : String(value),
  fromAttribute: (value: string | null) =>
    value === null ? undefined : value !== 'false',
};

@customElement('ps-button')
export class ButtonWC
  extends Translate(
    FormControlMixin(
      SlotObserver(BaseElementWithGlobalStyles, [
        '[slot]',
        '[slot="prefix"]',
        '[slot="suffix"]',
      ])
    )
  )
  implements NonCheckableFormControl
{
  /** Checks for validity but does not show a validation message. Returns `true` when valid and `false` when invalid. */
  checkValidity() {
    if (this.isButton()) {
      return (this.button as HTMLButtonElement).checkValidity();
    }

    return true;
  }

  defaultValue?: unknown;

  defaultChecked?: boolean | undefined;

  pattern?: string | undefined;

  min?: number | string | Date;

  max?: number | string | Date;

  step?: number | 'any' | undefined;

  required?: boolean | undefined;

  minlength?: number | undefined;

  maxlength?: number | undefined;

  static styles = unsafeCSS(buttonStyles);

  static globalStyles = [unsafeCSS(buttonVars1), unsafeCSS(buttonVars2)];

  /** An optional value for the button. Ignored when `href` is set. */
  @property({ attribute: false }) value: unknown = '';

  @property() validationMode: FormControl['validationMode'] = 'onSubmit';

  /** Gets the validity state object */
  get validity() {
    if (this.isButton()) {
      return (this.button as HTMLButtonElement).validity;
    }

    return validValidityState;
  }

  /** Gets the validation message */
  get validationMessage() {
    if (this.isButton()) {
      return (this.button as HTMLButtonElement).validationMessage;
    }

    return '';
  }

  // The following properties and methods aren't strictly required,
  // but browser-level form controls provide them. Providing them helps
  // ensure consistency with browser-provided controls.
  /** Gets the associated form, if one exists. */
  getForm(): HTMLFormElement | null {
    return this.formControlController.getForm();
  }

  /** An optional name for the button. Ignored when `href` is set. */
  @property() name = '';

  /**
   * @method
   * @return {boolean} Returns true if internals's target element has no validity
   * problems; otherwise, returns false, fires an invalid event at the element, and (if
   * the event isn't canceled) reports the problem to the user.
   */
  reportValidity() {
    // return this.internals.reportValidity();
    if (this.isButton()) {
      return (this.button as HTMLButtonElement).reportValidity();
    }

    return true;
  }

  _variant: ButtonVariant = 'primary';

  set variant(newValue: ButtonVariant) {
    const oldVal = this._variant;
    if (newValue === oldVal) return;
    this._variant = newValue;
    this.requestUpdate('variant', oldVal);
  }

  /** The button's variant. */
  @property({ reflect: true, attribute: true })
  get variant() {
    return this._variant;
  }

  /** The button's data-testid. */
  @property({ reflect: true }) 'data-testid'?: string = 'wc-button';

  /** The width's option. */
  @property({ reflect: true }) width: 'auto' | 'full' = 'auto';

  /** The button's size. */
  @property({ reflect: true }) size: 'small' | 'medium' | 'large' | 'xlarge' =
    'medium';

  /** Set button rounded shape. */
  @property({ reflect: true, type: Boolean }) rounded = false;

  /** Disables the button. */
  @property({ reflect: true, type: Boolean }) disabled?: boolean = false;

  /**
   * Set button selected state.
   *
   * VISUAL ONLY — it paints the selected styling and says nothing to assistive
   * technology. It predates {@link pressed} and is used both for toggles and
   * for "this is the current one" buttons, so mapping it to a single ARIA state
   * would change what AT announces about UI nobody touched. A toggle button
   * should use {@link pressed}, which paints the same state AND exposes it.
   */
  @property({ reflect: true, type: Boolean }) selected: boolean = false;

  /** Set button loading state. */
  @property({ reflect: true, type: Boolean }) loading: boolean = false;

  /**
   * Accessible name for the inner `<button>` / `<a>` (`aria-label`).
   *
   * The interactive node lives in this component's shadow root, so an
   * `aria-label` written on the `<ps-button>` tag never reaches it — a
   * consumer has no other way to name an icon-only button (WCAG 1.1.1, 4.1.2).
   * Prefer visible text in the default slot; reach for this only when the
   * button carries no text (an icon, a badge count), because it OVERRIDES any
   * slotted label for AT.
   */
  @property() label?: string;

  /**
   * Expanded state of the collapsible thing this button controls
   * (`aria-expanded`) — a popover, dropdown, menu, disclosure.
   *
   * Tri-state on purpose: left `undefined` nothing is rendered (the button
   * controls nothing), while `false` is a real, announced state ("collapsed")
   * and must be set as soon as the button owns a disclosure at all.
   */
  @property({ reflect: true, converter: triStateBoolean })
  expanded?: boolean;

  /**
   * The kind of popup this button opens (`aria-haspopup`), for a trigger whose
   * activation reveals a menu, listbox, tree, grid or dialog. Pair it with
   * {@link expanded}.
   */
  @property({ reflect: true, attribute: 'has-popup' }) hasPopup?:
    | 'true'
    | 'menu'
    | 'listbox'
    | 'tree'
    | 'grid'
    | 'dialog';

  /**
   * Toggle state of a two-state button (`aria-pressed`), e.g. a toolbar toggle
   * or a trigger that stays visibly on. Rendering it also applies the
   * {@link selected} styling, so a toggle needs this property alone.
   *
   * Tri-state on purpose: `undefined` renders nothing (a plain action button
   * must NOT be announced as a toggle), `false` announces "not pressed".
   *
   * `aria-pressed` rather than `role="switch"` + `aria-checked`: a switch is an
   * on/off setting that takes effect immediately and it REPLACES the button
   * role, so every existing consumer of this component — menu and popover
   * triggers, tab-like buttons, form submits — would start announcing as
   * something it is not, and `aria-checked` cannot be combined with the
   * `aria-expanded` a trigger needs. `aria-pressed` keeps `role="button"` and
   * adds the pressed state on top, which is exactly what a button with a
   * persistent visual "on" look is.
   */
  @property({ reflect: true, converter: triStateBoolean })
  pressed?: boolean;

  /**
   * The type of button. When the type is `submit`, the button will submit the surrounding form. Note that the default
   * value is `button` instead of `submit`, which is opposite of how native `<button>` elements behave.
   */
  @property({ reflect: true }) type?: 'button' | 'submit' | 'reset' = 'button';

  /** When set, the underlying button will be rendered as an `<a>` with this `href` instead of a `<button>`. */
  @property() href?: HTMLAnchorElement['href'];

  /** If button rendered as an `<a>`, it is possible to set target attribute. */
  @property({ reflect: true }) target?: HTMLAnchorElement['target'];

  private readonly formControlController = new FormControlController(this, {
    assumeInteractionOn: ['click'],
    form: (input) => {
      // Buttons support a form attribute that points to an arbitrary form, so if this attribute it set we need to query
      // the form from the same root using its id
      if (input.hasAttribute('form')) {
        const doc = input.getRootNode() as Document | ShadowRoot;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const formId = input.getAttribute('form')!;
        return doc.getElementById(formId) as HTMLFormElement;
      }

      // Fall back to the closest containing form
      return input.closest('form');
    },
  });

  private handleInvalid(event: Event) {
    this.formControlController.setValidity(false);
    this.formControlController.emitInvalidEvent(event);
  }

  @query('.c-btn') button?: HTMLButtonElement | HTMLLinkElement;

  /** Sets a custom validation message. If `message` is not empty, the field will be considered invalid. */
  setCustomValidity(message: string) {
    if (this.isButton()) {
      (this.button as HTMLButtonElement).setCustomValidity(message);
      this.formControlController.updateValidity();
    }
  }

  @state() invalid = false;

  handleClick(event: MouseEvent) {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // if the React implementation prevents default when an onClick is detected, we should intelligently respond to allow that
    if (event.defaultPrevented === true) {
      event.preventDefault();
      return;
    }

    if (this.type === 'submit') {
      this.formControlController.submit(this);
    }

    if (this.type === 'reset') {
      this.formControlController.reset(this);
    }
  }

  private isButton() {
    return !this.href;
  }

  private isLink() {
    return !!this.href;
  }

  firstUpdated(
    changedProperties: PropertyValueMap<unknown> | Map<PropertyKey, unknown>
  ) {
    super.firstUpdated(changedProperties);
    this.addEventListener('click', this.handleClick);
  }

  // workaround to the make sure the extendable `variant` prop's attribute is initially set when booting up
  connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    this.setAttribute('variant', this.variant);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    // eslint-disable-next-line wc/guard-super-call
    super.disconnectedCallback();
  }

  focus(options?: FocusOptions) {
    (this.button as HTMLButtonElement).focus(options);
  }

  render() {
    const isLink = this.isLink();
    const tag = isLink ? literal`a` : literal`button`;

    return html`
      <${tag}
        class="${classMap({
          'c-btn': true,
          [`c-btn--variant-${this.variant}`]: this.variant,
          [`c-btn--size-${this.size}`]: this.size,
          [`c-btn--width-${this.width}`]: this.width,
          'c-btn--rounded': this.rounded,
          'c-btn--selected': this.selected || this.pressed === true,
          'c-btn--loading': this.loading,
        })}"
        ?disabled=${ifDefined(isLink ? undefined : this.disabled)}
        aria-busy=${this.loading ? 'true' : nothing}
        aria-label=${ifDefined(this.label || undefined)}
        aria-expanded=${
          this.expanded === undefined ? nothing : String(this.expanded)
        }
        aria-haspopup=${ifDefined(this.hasPopup)}
        aria-pressed=${
          this.pressed === undefined ? nothing : String(this.pressed)
        }
        href=${ifDefined(isLink ? this.href : undefined)}
        target=${ifDefined(isLink ? this.target : undefined)}
        type=${ifDefined(isLink ? undefined : this.type)}
        name="${this.name}"
        value=${ifDefined(isLink ? undefined : this.value)}
        part="button"
        @invalid=${this.isButton() ? this.handleInvalid : null}
        @click=${this.handleClick}
        exportparts="prefix"
      >
        <span class="c-btn__inner">
          ${
            this.hasSlottedContent('[slot="prefix"]')
              ? html`
                  <slot
                    name="prefix"
                    class="c-btn__prefix"
                    part="prefix"
                  ></slot>
                `
              : nothing
          }
          <span class="c-btn__label">
            <slot></slot>
          </span>
          ${
            this.hasSlottedContent('[slot="suffix"]') && !this.loading
              ? html`
                  <slot
                    name="suffix"
                    class="c-btn__suffix"
                    part="suffix"
                  ></slot>
                `
              : nothing
          }
          ${this.loading ? html`<span class="c-btn__loader"></span>` : nothing}
        </span>
      </${tag}>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-button': ButtonWC;
  }
  enum PSElementTagNameMap {
    'ps-button' = 'ps-button',
  }
}
